// src/components/winback/SendWinbackModal.jsx
import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faTimes,
    faPaperPlane,
    faSpinner,
    faLightbulb
} from '@fortawesome/free-solid-svg-icons';
import { get, post } from '../../helpers/apiHelper';
import { useWinbackConfig } from '../../hooks/useWinbackConfig';
import Swal from 'sweetalert2';

const SendWinbackModal = ({ cliente, onClose, onSuccess }) => {
    const [recommendation, setRecommendation] = useState(null);
    const [loading, setLoading] = useState(true);
    const [sending, setSending] = useState(false);
    const { config } = useWinbackConfig(cliente?.marcaId);

    useEffect(() => {
        const loadRecommendation = async () => {
            try {
                const data = await get(`clientes/winback/recommendation/${cliente.clienteId}`);
                setRecommendation(data);
            } catch (error) {
                console.error('Error loading recommendation:', error);
            } finally {
                setLoading(false);
            }
        };

        loadRecommendation();
    }, [cliente.clienteId]);

    const handleSend = async () => {
        setSending(true);
        try {
            await post(`clientes/winback/send/${cliente.clienteId}`, {
                channel: 'email'
            });
            onSuccess();
        } catch (error) {
            console.error('Error sending winback:', error);
            Swal.fire({
                title: 'Error',
                text: error.message || 'No se pudo enviar el WinBack',
                icon: 'error'
            });
            setSending(false);
        }
    };

    return (
        <div className="fixed inset-0 z-50 overflow-y-auto">
            <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
                {/* Overlay */}
                <div
                    className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75"
                    onClick={onClose}
                ></div>

                {/* Modal */}
                <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
                    {/* Header */}
                    <div className="bg-gradient-to-r from-purple-600 to-indigo-600 px-6 py-4">
                        <div className="flex items-center justify-between">
                            <h3 className="text-lg font-medium text-white">
                                Enviar WinBack
                            </h3>
                            <button
                                onClick={onClose}
                                className="text-white hover:text-gray-200 transition-colors"
                            >
                                <FontAwesomeIcon icon={faTimes} className="text-xl" />
                            </button>
                        </div>
                    </div>

                    {/* Body */}
                    <div className="px-6 py-4">
                        {/* Información del Cliente */}
                        <div className="mb-6">
                            <h4 className="text-sm font-medium text-gray-700 mb-3">
                                Información del Cliente
                            </h4>
                            <div className="bg-gray-50 rounded-lg p-4 space-y-2">
                                <div className="flex justify-between">
                                    <span className="text-sm text-gray-600">Nombre:</span>
                                    <span className="text-sm font-medium text-gray-900">
                                        {cliente.nombre} {cliente.apellido}
                                    </span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-sm text-gray-600">Email:</span>
                                    <span className="text-sm font-medium text-gray-900">
                                        {cliente.email}
                                    </span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-sm text-gray-600">Días Inactivo:</span>
                                    <span className="text-sm font-bold text-red-600">
                                        {cliente.daysInactive} días
                                    </span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-sm text-gray-600">Segmento:</span>
                                    <span className="text-sm font-medium text-purple-600">
                                        {cliente.segment}
                                    </span>
                                </div>
                            </div>
                        </div>

                        {/* Recomendación del Sistema */}
                        {loading ? (
                            <div className="flex justify-center py-8">
                                <FontAwesomeIcon icon={faSpinner} spin className="text-3xl text-purple-600" />
                            </div>
                        ) : recommendation ? (
                            <div className="mb-6">
                                <div className="flex items-center gap-2 mb-3">
                                    <FontAwesomeIcon icon={faLightbulb} className="text-yellow-500" />
                                    <h4 className="text-sm font-medium text-gray-700">
                                        Recomendación del Sistema
                                    </h4>
                                </div>
                                <div className="bg-gradient-to-r from-purple-50 to-indigo-50 border border-purple-200 rounded-lg p-4 space-y-3">
                                    <div className="grid grid-cols-2 gap-4">
                                        <div>
                                            <p className="text-xs text-gray-600">Umbral Recomendado</p>
                                            <p className="text-lg font-bold text-purple-900">
                                                {recommendation.recommendedThreshold} días
                                            </p>
                                        </div>
                                        <div>
                                            <p className="text-xs text-gray-600">Frecuencia del Cliente</p>
                                            <p className="text-lg font-bold text-purple-900">
                                                {recommendation.customerFrequency ?
                                                    `${recommendation.customerFrequency.toFixed(1)} días` :
                                                    'N/A'
                                                }
                                            </p>
                                        </div>
                                    </div>
                                    <div>
                                        <p className="text-xs text-gray-600 mb-1">Explicación:</p>
                                        <p className="text-xs text-gray-700 leading-relaxed">
                                            {recommendation.reason}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        ) : null}

                        {/* Mensaje de Advertencia */}
                        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
                            <p className="text-xs text-yellow-800 mb-2">
                                <strong>Protección Anti-Spam:</strong> Solo se puede enviar un WinBack cada {config?.cooldownPeriod || 30} días al mismo cliente.
                            </p>
                            <p className="text-xs text-yellow-800">
                                <strong>Consentimiento:</strong> Solo se contactará a clientes que hayan aceptado recibir comunicaciones de marketing.
                            </p>
                        </div>
                    </div>

                    {/* Footer */}
                    <div className="bg-gray-50 px-6 py-4 flex flex-col-reverse sm:flex-row sm:justify-end gap-3">
                        <button
                            onClick={onClose}
                            disabled={sending}
                            className="w-full sm:w-auto px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 disabled:opacity-50"
                        >
                            Cancelar
                        </button>
                        <button
                            onClick={handleSend}
                            disabled={sending}
                            className="w-full sm:w-auto inline-flex justify-center items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 disabled:opacity-50"
                        >
                            {sending ? (
                                <>
                                    <FontAwesomeIcon icon={faSpinner} spin className="mr-2" />
                                    Enviando...
                                </>
                            ) : (
                                <>
                                    <FontAwesomeIcon icon={faPaperPlane} className="mr-2" />
                                    Enviar WinBack
                                </>
                            )}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default SendWinbackModal;

